"use client";

import React, { useState } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal } from "@/components/shared/motion";
import { MapPin, CalendarDays, Wifi, ArrowRight, Check, ChevronLeft, Smartphone, Camera, MessageCircle, Video } from "lucide-react";

const EXPERIMENT_ID = "wf_beginner_quiz_v1";

type Step = "destination" | "duration" | "usage" | "result";
type Duration = "1-3" | "4-7" | "8-14" | "15+";
type Usage = "maps" | "social" | "streaming" | "hotspot";

const DESTINATIONS = [
  { code: "th", name: "Thailand" },
  { code: "jp", name: "Japan" },
  { code: "us", name: "USA" },
  { code: "fr", name: "France" },
  { code: "tr", name: "Turkey" },
  { code: "es", name: "Spain" },
  { code: "id", name: "Bali / Indonesia" },
  { code: "gr", name: "Greece" },
  { code: "mx", name: "Mexico" },
];

const DURATIONS: { value: Duration; label: string }[] = [
  { value: "1-3", label: "1–3 days (weekend)" },
  { value: "4-7", label: "4–7 days (short trip)" },
  { value: "8-14", label: "8–14 days (two weeks)" },
  { value: "15+", label: "15+ days (long stay)" },
];

const USAGES: { value: Usage; icon: typeof MessageCircle; label: string; desc: string }[] = [
  { value: "maps", icon: MapPin, label: "Maps & messaging", desc: "Google Maps, WhatsApp, emails" },
  { value: "social", icon: Camera, label: "Photos & social", desc: "Instagram, TikTok, photos" },
  { value: "streaming", icon: Video, label: "Video calls & streaming", desc: "Zoom, YouTube, Netflix" },
  { value: "hotspot", icon: Smartphone, label: "Hotspot for family", desc: "Share data with others" },
];

function recommendGb(duration: Duration, usage: Usage): string {
  const days = duration === "1-3" ? 3 : duration === "4-7" ? 7 : duration === "8-14" ? 14 : 30;
  const base = usage === "maps" ? 1 : usage === "social" ? 3 : usage === "streaming" ? 5 : 3;
  const gb = Math.min(Math.ceil(base * (days / 7)), 30);
  return `${gb}GB / ${days} days`;
}

function recommendLabel(gb: number): string {
  if (gb <= 3) return "Light Trip";
  if (gb <= 5) return "Explorer";
  if (gb <= 10) return "Adventure";
  return "Long Stay";
}

export default function HelpMeChoose() {
  const { t } = useTranslations();
  const [step, setStep] = useState<Step>("destination");
  const [destination, setDestination] = useState("");
  const [duration, setDuration] = useState<Duration | "">("");
  const [usage, setUsage] = useState<Usage | "">("");

  const fire = (event: string) => {
    try { track({ event_name: event as any, event_id: `${event}:${EXPERIMENT_ID}:${Date.now()}`, experiment_id: EXPERIMENT_ID }); } catch {}
  };

  const canNext = step === "destination" ? destination : step === "duration" ? duration : usage;
  const nextStep = () => {
    if (step === "destination") { fire("help_me_choose_started"); setStep("duration"); }
    else if (step === "duration") setStep("usage");
    else if (step === "usage") { setStep("result"); fire("recommendation_completed"); }
  };
  const prevStep = () => {
    if (step === "duration") setStep("destination");
    else if (step === "usage") setStep("duration");
    else if (step === "result") setStep("usage");
  };
  const reset = () => { setStep("destination"); setDestination(""); setDuration(""); setUsage(""); };

  const destName = DESTINATIONS.find(d => d.code === destination)?.name || "";
  const recommended = duration && usage ? recommendGb(duration as Duration, usage as Usage) : "";
  const [gbStr] = recommended ? recommended.split("/") : ["5GB"];
  const gb = parseInt(gbStr) || 5;

  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="wander-card p-6 sm:p-10 max-w-2xl mx-auto" y={18}>
        {/* progress dots */}
        {step !== "result" && (
          <div className="flex items-center justify-center gap-2 mb-8">
              {(["destination", "duration", "usage"] as const).map((s, i) => {
                const displayStep: "destination" | "duration" | "usage" = (step as string) === "result" ? "usage" : step as "destination" | "duration" | "usage";
                const idx = ["destination", "duration", "usage"].indexOf(displayStep);
                return (<React.Fragment key={s}>
                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${s === displayStep ? "bg-primary text-white" : idx > i ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"}`}>
                  {idx > i ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                {i < 2 && <span className="w-8 sm:w-12 h-px bg-muted-foreground/20" />}
              </React.Fragment>);})}
          </div>
        )}

        {step === "destination" && (
          <>
            <h2 className="text-xl font-extrabold text-foreground mb-2">{t("wander.quiz.q1", "Where are you going?")}</h2>
            <p className="text-sm text-muted-foreground mb-5">{t("wander.quiz.q1sub", "Pick your destination and we'll find the right eSIM for you.")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DESTINATIONS.map((d) => (
                <button key={d.code} type="button" onClick={() => { setDestination(d.code); }} className={`p-3 rounded-xl border text-sm font-semibold text-left transition-colors ${destination === d.code ? "border-primary bg-primary/5 text-primary" : "border-border text-foreground hover:border-primary/40"}`}>
                  {d.name}
                </button>
              ))}
            </div>
          </>
        )}

        {step === "duration" && (
          <>
            <h2 className="text-xl font-extrabold text-foreground mb-2">{t("wander.quiz.q2", "How long is your trip?")}</h2>
            <p className="text-sm text-muted-foreground mb-5">{t("wander.quiz.q2sub", "Choose your trip length and we'll match the plan validity.")}</p>
            <div className="space-y-2">
              {DURATIONS.map((d) => (
                <button key={d.value} type="button" onClick={() => setDuration(d.value)} className={`w-full p-4 rounded-xl border text-sm font-semibold text-left flex items-center justify-between transition-colors ${duration === d.value ? "border-primary bg-primary/5 text-primary" : "border-border text-foreground hover:border-primary/40"}`}>
                  <span>{d.label}</span>
                  {duration === d.value && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
          </>
        )}

        {step === "usage" && (
          <>
            <h2 className="text-xl font-extrabold text-foreground mb-2">{t("wander.quiz.q3", "What will you use data for?")}</h2>
            <p className="text-sm text-muted-foreground mb-5">{t("wander.quiz.q3sub", "Tell us how you'll use the internet — we'll recommend the right data amount.")}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {USAGES.map((u) => (
                <button key={u.value} type="button" onClick={() => setUsage(u.value)} className={`p-4 rounded-xl border text-left transition-colors ${usage === u.value ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-foreground"><u.icon className="w-4 h-4 text-primary" />{u.label}</span>
                  <p className="text-xs text-muted-foreground mt-1">{u.desc}</p>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "result" && (
          <div className="text-center">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary mb-4"><Wifi className="w-7 h-7" /></span>
            <h2 className="text-xl font-extrabold text-foreground">{t("wander.quiz.result", "We recommend")}</h2>
            <div className="mt-4 p-6 rounded-2xl border-2 border-primary bg-primary/5">
              <div className="text-3xl font-extrabold text-primary">{recommended || "5GB / 7 days"}</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{recommendLabel(gb)} Plan</div>
              <div className="mt-1 text-xs text-muted-foreground">Good for {destName || "your trip"}</div>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">{t("wander.quiz.resultSub", "Browse plans for your destination to see all options and pick what fits best.")}</p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link href="/destinations/" className="inline-flex items-center gap-2 rounded-full bg-primary text-white font-bold px-7 h-12 hover:bg-primary/90 transition-colors" onClick={() => fire("recommendation_accepted")}>{t("wander.quiz.browse", "Browse plans")} <ArrowRight className="w-4 h-4" /></Link>
              <button type="button" onClick={reset} className="inline-flex items-center gap-2 rounded-full border border-border text-foreground font-semibold px-6 h-12 hover:bg-muted transition-colors">{t("wander.quiz.restart", "Start over")}</button>
            </div>
          </div>
        )}

        {/* navigation buttons */}
        {step !== "result" && (
          <div className="mt-8 flex items-center justify-between">
            <div>
              {step !== "destination" && (
                <button type="button" onClick={prevStep} className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground/70 hover:text-foreground">
                  <ChevronLeft className="w-4 h-4" /> {t("wander.quiz.back", "Back")}
                </button>
              )}
            </div>
            <button type="button" onClick={nextStep} disabled={!canNext} className={`inline-flex items-center gap-2 rounded-full font-semibold px-7 h-11 transition-colors ${canNext ? "bg-primary text-white hover:bg-primary/90" : "bg-muted text-muted-foreground cursor-not-allowed"}`}>
              {step === "usage" ? t("wander.quiz.seeResult", "See my plan") : t("wander.quiz.next", "Next")} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </Reveal>
    </section>
  );
}
